import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@/components/ui/icons";

import { cn } from "@/lib/utils";

/**
 * Slide-out side panel — same Radix `Dialog` primitive as
 * `DialogContent`, just positioned against an edge instead of centered.
 * Used by the mobile responsive flips:
 *
 *   - Sidebar renders inside a `Sheet side="left"` on phone-sized
 *     viewports (`useIsMobile`); on desktop it stays a flex column.
 *   - ThreadPanel / MemberListPanel / PinnedMessagesPanel render inside
 *     `Sheet side="right"` on mobile; on desktop they stay flex siblings.
 *
 * Builds on `DialogPrimitive` so the Portal + Overlay + focus-trap +
 * Esc-to-close come for free.
 */

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

function SheetOverlay({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Overlay>>;
}) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-overlay/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

const SHEET_SIDE_CLASSES = {
  left: "inset-y-0 left-0 h-full w-[85vw] max-w-sm border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
  right:
    "inset-y-0 right-0 h-full w-[85vw] max-w-sm border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
} as const;

type SheetSide = keyof typeof SHEET_SIDE_CLASSES;

interface SheetContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  side?: SheetSide;
  /** Whether to render the close X in the corner. Default true. */
  showClose?: boolean;
}

function SheetContent({
  className,
  children,
  side = "left",
  showClose = true,
  ref,
  ...props
}: SheetContentProps & {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 border-stroke-card bg-modal-opaque shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
          SHEET_SIDE_CLASSES[side],
          className,
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close className="absolute right-3 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
}

export { Sheet, SheetTrigger, SheetClose, SheetPortal, SheetContent };
